import React, { useState, useEffect } from "react";
import { MedsPage } from "./modules/MedsPage";
import { DietPage } from "./modules/DietPage";
import { MeasurePage } from "./modules/MeasurePage";
import { WalkPage } from "./modules/WalkPage";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { LoginModal } from "./components/LoginModal";
import { ProtectedContent } from "./components/ProtectedClick";
import { SettingsPage } from "./components/SettingsPage";
import { CustomerCenterPage } from "./components/CustomerCenterPage";
import { EnvironmentSettingsPage } from "./components/EnvironmentSettingsPage";
import { PersonalSettingsPage } from "./components/PersonalSettingsPage";
import { EventNoticeListPage } from "./components/EventNoticeListPage";
import { MedicationHistoryPage } from "./components/MedicationHistoryPage";
import { DietHistoryPage } from "./components/DietHistoryPage";
import { BloodRecordsPage } from "./components/BloodRecordsPage";
import { HealthAdviceCard } from "./components/HealthAdviceCard";
import { HealthDiaryModal } from "./components/HealthDiaryModal";
import { AIChatbotComponent } from "./components/AIChatbotComponent";
import MyInfoPage from "./components/MyInfoPage";
import { BodyMeasurementsPage } from "./pages/Settings/BodyMeasurements";
import HealthDiaryPage from "./components/HealthDiaryPage";


// ===== 공통 유틸 =====
const formatNumber = (n: number | null | undefined) => {
  if (n == null || Number.isNaN(n as number)) return "-";
  return (n as number).toLocaleString("ko-KR");
};


// type SymptomDraft = {
//   symptomText: string;
//   bodyPart: string;
//   since: "오늘" | "어제" | "2~3일 전" | "일주일 이상";
//   severity: "가벼움" | "보통" | "심함";
//   bleeding: boolean;
//   pain: boolean;
//   fever: boolean;
//   notes: string;
// };


// Preview 환경에서만 실행 (SSR 안전)
if (typeof window !== "undefined") {
  // 중복 실행 방지
  (window as any).__HERBY_UI_TESTED__ = (window as any).__HERBY_UI_TESTED__ ?? false;
  if (!(window as any).__HERBY_UI_TESTED__) {
    (window as any).__HERBY_UI_TESTED__ = true;    
  }
}

// ===== 공통 카드 래퍼 =====
const SectionCard: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className = "" }) => (
  <div className={`mx-4 mt-3 rounded-2xl bg-white shadow-sm px-4 py-3 mb-3 border border-slate-100 ${className}`}>{children}</div>
);

// ===== 간단 페이지 헤더(탭 전환 데모용) =====
const PageHeader: React.FC<{ title: string; subtitle?: string; onBack?: () => void; right?: React.ReactNode }> = ({
  title,
  subtitle,
  onBack,
  right,
}) => (
  <header className="sticky top-0 z-30 flex items-center justify-between px-5 py-3 bg-white shadow-sm">
    <div className="flex items-center gap-2">
      {onBack ? (
        <button
          type="button"
          onClick={onBack}
          className="w-9 h-9 rounded-xl bg-slate-100 text-slate-700 flex items-center justify-center"
          title="뒤로"
        >
          ←
        </button>
      ) : (
        <div className="w-9" />
      )}
      <div className="flex flex-col">
        <span className="text-[15px] font-extrabold text-slate-900">{title}</span>
        {subtitle ? <span className="text-[10px] text-slate-400">{subtitle}</span> : null}
      </div>
    </div>
    <div className="flex items-center gap-2">{right}</div>
  </header>
);

// ===== 오늘의 건강요약(결론) =====
// 분리된 컴포넌트에서 import

// ===== 지금 먼저 확인할 것(오늘의 건강미션) — 단일 카드(중복 제거의 핵심) =====

type NextAction = {
  key: string;
  icon: string;
  title: string;
  statusLabel: string;
  statusTone: "good" | "warn" | "neutral";
  reasonShort: string;
  value: string;
  ctaLabel: string;
  onClick?: () => void;
};

const NextActionsCard: React.FC<{ title: string; subtitle: string; items: NextAction[] }> = ({ title, subtitle, items }) => {
  const toneBadge = (tone: NextAction["statusTone"]) => {
    if (tone === "good") return "bg-emerald-50 text-emerald-700 border-emerald-200";
    if (tone === "warn") return "bg-amber-50 text-amber-700 border-amber-200";
    return "bg-slate-50 text-slate-600 border-slate-200";
  };

  return (
    <section className="mx-4 mt-3 rounded-2xl bg-white border border-violet-200 shadow-sm px-4 py-3 text-slate-900">
      <div className="flex items-center justify-between mb-2">
        <div>
          <div className="text-[13px] font-extrabold text-slate-900 flex items-center gap-1.5">
            <span className="text-[16px]">⭐</span>
            <span>{title}</span>
          </div>
          <div className="mt-0.5 text-[11px] text-slate-500">{subtitle}</div>
        </div>
        <div className="text-[11px] text-slate-500">{items.length}개</div>
      </div>

      <div className="flex flex-col gap-2 mt-2">
        {items.map((m) => (
          <button
            key={m.key}
            type="button"
            onClick={m.onClick}
            className="w-full flex items-center justify-between rounded-xl border border-slate-200 px-3 py-2.5 text-left transition-colors bg-white active:bg-slate-50"
          >
            <div className="flex items-start gap-2">
              <div className="mt-0.5 flex items-center justify-center w-6 h-6 rounded-full text-[14px] bg-violet-50 shadow-sm text-violet-700">
                {m.icon}
              </div>
              <div className="flex flex-col">
                <div className="flex items-center gap-2">
                  <span className="text-[12px] font-semibold text-slate-900">{m.title}</span>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full border ${toneBadge(m.statusTone)}`}>{m.statusLabel}</span>
                </div>
                <span className="mt-0.5 text-[11px] text-slate-500">{m.reasonShort}</span>
              </div>
            </div>
            <div className="flex flex-col items-end text-right">
              <span className="text-[12px] font-bold text-slate-900">{m.value}</span>
              <span className="mt-0.5 text-[10px] text-violet-700 font-semibold underline">{m.ctaLabel}</span>
            </div>
          </button>
        ))}
      </div>
    </section>
  );
};

// ===== 꾸준함 점수보기 (포인트/리워드 후면화 — 기본 접힘) =====
// 분리된 컴포넌트에서 import

// ===== 서브 페이지(복약/식단/측정/걷기) — 데모용 풀흐름 =====
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const SimpleModulePage: React.FC<{
  title: string;
  subtitle: string;
  icon: string;
  onBack: () => void;
  children?: React.ReactNode;
}> = ({ title, subtitle, icon, onBack, children }) => {
  return (
    <div className="w-full max-w-md mx-auto min-h-screen bg-slate-100 pb-28 relative font-[system-ui]">
      <PageHeader
        title={title}
        subtitle={subtitle}
        onBack={onBack}
        right={<span className="w-9 h-9 rounded-xl bg-violet-50 text-violet-700 flex items-center justify-center">{icon}</span>}
      />
      <div className="px-4 pt-3">
        <SectionCard>
          <p className="text-[13px] font-semibold text-slate-900 mb-1">{title} (데모 화면)</p>
          <p className="text-[12px] text-slate-600 leading-relaxed">
            이 탭은 현재 “탭 전환이 되는지/흐름이 끊기지 않는지”를 확인하기 위한 데모 페이지입니다. 실제 서비스에서는 해당 모듈의
            풀 UI(캘린더/기록/통계/입력/알림)를 이 영역에 연결하면 됩니다.
          </p>
          <div className="mt-2 flex gap-2">
            <button type="button" onClick={onBack} className="flex-1 h-11 rounded-xl bg-violet-600 text-white text-[12px] font-semibold">
              홈으로 돌아가기
            </button>
            <button type="button" className="flex-1 h-11 rounded-xl border border-slate-200 bg-white text-slate-700 text-[12px] font-semibold">
              기록 추가(데모)
            </button>
          </div>
        </SectionCard>
        {children}
      </div>

      <nav className="fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-slate-200">
        <div className="w-full max-w-md mx-auto flex items-center justify-around py-2 text-[11px] text-slate-600">
          <button type="button" onClick={onBack} className="flex flex-col items-center gap-0.5 px-3 py-1 rounded-xl text-violet-700 bg-violet-50">
            <span className="text-lg">🏠</span>
            <span>홈</span>
          </button>
          <span className="text-[11px] text-slate-400">탭 전환 데모</span>
        </div>
      </nav>
    </div>
  );
};

// ===== 공통 헤더 컴포넌트 =====
const CommonHeader: React.FC<{
  onLogoClick: () => void;
  onDiary: () => void;
  onFamily: () => void;
  onShare: () => void;
  onSettings: () => void;
  isLoggedIn: boolean;
  user: any;
  onLogout: () => void;
}> = ({ onLogoClick, onDiary, onFamily, onShare, onSettings, isLoggedIn, user, onLogout }) => (
  <header className="sticky top-0 z-30 w-full flex items-center justify-between px-4 py-3 bg-slate-100">
    <button type="button" onClick={onLogoClick} className="flex items-center gap-2 cursor-pointer flex-shrink-0">
      <div className="w-8 h-8 rounded-lg bg-violet-600 text-white flex items-center justify-center text-lg font-bold shadow-sm flex-shrink-0">
        <img src="/logo.png" alt="허비서AI 로고" className="w-6 h-6" />
      </div>
      <div className="flex flex-col">
        <span className="text-[16px] font-extrabold text-violet-700">허비서AI</span>
        <span className="text-[10px] text-slate-400">나만의 건강 주치의</span>
      </div>
    </button>
    <div className="flex items-center gap-4 text-[10px] text-slate-600">
      <button className="flex flex-col items-center gap-0.5" type="button" onClick={onDiary}>
        <span className="w-6 h-6 rounded-full bg-violet-100 flex items-center justify-center text-[14px]">📝</span>
        <span>일기</span>
      </button>
      <button className="flex flex-col items-center gap-0.5" type="button" onClick={onFamily}>
        <span className="w-6 h-6 rounded-full bg-violet-100 flex items-center justify-center text-[14px]">👨‍👩‍👧‍👦</span>
        <span>가족</span>
      </button>
      <button className="flex flex-col items-center gap-0.5" type="button" onClick={onShare}>
        <span className="w-6 h-6 rounded-full bg-violet-100 flex items-center justify-center text-[14px]">📤</span>
        <span>공유</span>
      </button>
      <button className="flex flex-col items-center gap-0.5" type="button" onClick={onSettings}>
        <span className="w-6 h-6 rounded-full bg-violet-100 flex items-center justify-center text-[14px]">⚙️</span>
        <span>설정</span>
      </button>      
    </div>
  </header>
);

// ===== 공통 하단 탭 네비게이션 =====
const BottomTabNavigation: React.FC<{
  activeTab: string;
  onTabChange: (tab: string) => void;
  isLoggedIn?: boolean;
  onLoginRequired?: () => void;
}> = ({ activeTab, onTabChange, isLoggedIn = true, onLoginRequired }) => {
  const tabs = [
    { id: "home", icon: "🏠", label: "홈" },
    { id: "meds", icon: "💊", label: "복약" },
    { id: "diet", icon: "🍽️", label: "식단" },
    { id: "measure", icon: "🩺", label: "측정" },
    { id: "walk", icon: "🚶", label: "걷기" },
  ];

  const handleTabClick = (tabId: string) => {
    if (tabId !== "home" && !isLoggedIn) {
      onLoginRequired?.();
      return;
    }
    onTabChange(tabId);
  };

  return (
    <nav className="fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-slate-200">
      <div className="w-full flex items-center justify-around py-2 px-4">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => handleTabClick(tab.id)}
            className={`flex flex-col items-center gap-0.5 px-3 py-1 rounded-xl transition-colors ${
              activeTab === tab.id
                ? "text-violet-700 bg-violet-50"
                : "text-slate-600 hover:bg-slate-50"
            }`}
          >
            <span className="text-lg">{tab.icon}</span>
            <span className="text-[11px]">{tab.label}</span>
          </button>
        ))}
      </div>
    </nav>
  );
};

// ===== 메인 컴포넌트 =====

function HerbyMainHomeContent() {
  const { user, logout, todayMeds, todayHealth, loadHealthByDate, loadMedsByDate, isLoggedIn, setShowLoginModal } = useAuth();
  const [activeTab, setActiveTab] = useState<string>("home");
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedDateData, setSelectedDateData] = useState<any>(null);
  const [showFutureWarning, setShowFutureWarning] = useState(false);
  const [profileCheckDone, setProfileCheckDone] = useState(false);
  const [weekStartDate, setWeekStartDate] = useState<Date>(() => {
    const d = new Date();
    d.setDate(d.getDate() - d.getDay());
    return d;
  });

  // 선택된 날짜의 복약 데이터
  const [selectedDateMeds, setSelectedDateMeds] = useState<any>(null);

  // 프로필 완성도 체크: 로그인 후 필수 필드 미완성이면 MyInfo로 리다이렉트
  React.useEffect(() => {
    if (!isLoggedIn || !user || profileCheckDone) return;

    // 프로필이 불완전하면 MyInfo 페이지로 자동 이동
    if (user.profileComplete === false) {
      setActiveTab("my-info");
    }
    
    setProfileCheckDone(true);
  }, [isLoggedIn, user, profileCheckDone]);

  // 탭 전환 시 페이지 스크롤을 상단으로 초기화
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [activeTab]);

  // 주의: API 호출은 AuthContext에서만 수행 (중복 방지)
  // Herbotai에서는 todayMeds, todayHealth context값을 사용하기만 함

  const [showShare, setShowShare] = useState<boolean>(false);
  const [showDiary, setShowDiary] = useState<boolean>(false);


  // const [showSymptomSheet, setShowSymptomSheet] = useState<boolean>(false);
  // const [symptomDraft, setSymptomDraft] = useState<SymptomDraft>({
  //   symptomText: "",
  //   bodyPart: "다리",
  //   since: "어제",
  //   severity: "보통",
  //   bleeding: false,
  //   pain: false,
  //   fever: false,
  //   notes: "",
  // });

  // DB에서 가져온 건강 데이터로 통계 구성 (선택된 날짜 데이터 또는 오늘 데이터)
  const displayData = selectedDateData || todayHealth;
  
  // 혈압 데이터 처리: 배열의 첫 번째 항목 사용 또는 미기록
  const getBloodPressureDisplay = () => {
    if (displayData?.bloodPressure && Array.isArray(displayData.bloodPressure) && displayData.bloodPressure.length > 0) {
      const bp = displayData.bloodPressure[0];
      return `${bp.systolic} / ${bp.diastolic}`;
    }
    return "미기록";
  };
  
  // 혈당 데이터 처리: 평균 계산 또는 미기록
  const getBloodGlucoseDisplay = () => {
    if (displayData?.bloodGlucose && Array.isArray(displayData.bloodGlucose) && displayData.bloodGlucose.length > 0) {
      const avg = Math.round(displayData.bloodGlucose.reduce((sum: number, bg: any) => {
        const glucoseValue = Number(bg.glucose_level) || 0;
        return sum + glucoseValue;
      }, 0) / displayData.bloodGlucose.length);
      return avg;
    }
    return "미기록";
  };
  
  const healthStats = {
    medsAdherence3d: todayMeds?.summary?.completionRate ?? 86,
    avgBp: getBloodPressureDisplay(),
    avgBg: getBloodGlucoseDisplay(),
    stepsToday: (displayData?.steps?.step_count ?? displayData?.steps?.count) ?? 0,
    stepsGoal: 7000,
  };

  const todaySummary = {
    healthScore: 82,
    scoreDiff: 3,
    riskLevel: "주의",
    keyMessages: ["아침 혈압은 괜찮은 편이에요.", "혈당이 목표 범위의 위쪽에 가까워서 식단이 중요해요.", "어제 걸음 수가 목표의 80% 정도였어요."],
    stats: healthStats,
  };

  // 선택된 날짜 또는 오늘 라벨
  const getDateLabel = () => {
    if (selectedDate) {
      const month = selectedDate.getMonth() + 1;
      const date = selectedDate.getDate();
      const dayLabels = ["일", "월", "화", "수", "목", "금", "토"];
      const day = dayLabels[selectedDate.getDay()];
      const isToday = selectedDate.toDateString() === new Date().toDateString();
      return `${isToday ? "오늘" : "선택"} (${month}/${date}, ${day})`;
    }
    const month = new Date().getMonth() + 1;
    const date = new Date().getDate();
    return `오늘 (${month}/${date}, 수)`;
  };
  
  const todayLabel = getDateLabel();
  
  // 선택된 날짜가 있으면 그 날짜의 데이터 사용, 아니면 오늘 데이터 사용
  const displayMeds = selectedDateMeds || todayMeds;
  
  const todayMedsData = displayMeds && displayMeds.summary
    ? { taken: displayMeds.summary.totalTaken, total: displayMeds.summary.totalScheduled } 
    : { taken: 0, total: 0 };
  
  // 로깅: 현재 약 복용 데이터 디버깅
  if (displayMeds?.summary) {
    console.log('[Herbotai] Meds data loaded:', displayMeds.summary);
  }
  
  // 동적 데이터: 선택된 날짜 또는 오늘 데이터
  const todaySteps = { 
    current: (displayData?.steps?.count ?? 0),
    goal: 7000 
  };
  
  // 식단 데이터: displayData에서 nutrition 객체의 칼로리
  const nutritionData = displayData?.nutrition;
  const totalNutritionCalories = nutritionData?.totalCalories || 0;
  const todayDiet = { 
    intakeKcal: Math.round(totalNutritionCalories),
    targetKcal: 1700 
  };

  const nextActions: NextAction[] = [
    {
      key: "meds",
      icon: "💊",
      title: "오늘 약 복용",
      statusLabel: todayMedsData.taken >= todayMedsData.total ? "완료" : "미확인",
      statusTone: todayMedsData.taken >= todayMedsData.total ? "good" : "warn",
      reasonShort: todayMedsData.taken >= todayMedsData.total ? "오늘 약은 잘 챙기셨어요" : "최근 3일 누락이 있어요",
      value: `${todayMedsData.taken}/${todayMedsData.total}회`,
      ctaLabel: "복약 기록",
      onClick: () => setActiveTab("meds"),
    },
    {
      key: "measure",
      icon: "🩺",
      title: "혈압·혈당 확인",
      statusLabel: todaySummary.stats.avgBp !== "미기록" && todaySummary.stats.avgBg !== "미기록" ? "확인됨" : "미확인",
      statusTone: todaySummary.stats.avgBp !== "미기록" && todaySummary.stats.avgBg !== "미기록" ? "good" : "warn",
      reasonShort: todaySummary.stats.avgBp !== "미기록" && todaySummary.stats.avgBg !== "미기록" ? "최근 기록은 안정적이에요" : "측정 기록이 없어요",
      value: `${todaySummary.stats.avgBp} / ${todaySummary.stats.avgBg}`,
      ctaLabel: "측정 보기",
      onClick: () => setActiveTab("measure"),
    },
    {
      key: "walk",
      icon: "🚶",
      title: "오늘 걸음",
      statusLabel: todaySteps.current >= todaySteps.goal ? "목표" : "부족",
      statusTone: todaySteps.current >= todaySteps.goal ? "good" : "warn",
      reasonShort: todaySteps.current >= todaySteps.goal ? "오늘 목표 달성!" : "목표까지 조금만 더",
      value: `${formatNumber(todaySteps.current)}/${formatNumber(todaySteps.goal)}보`,
      ctaLabel: "걷기 보기",
      onClick: () => setActiveTab("walk"),
    },
    {
      key: "diet",
      icon: "🍽️",
      title: "식단 기록",
      statusLabel: todayDiet.intakeKcal > 0 ? "기록됨" : "미확인",
      statusTone: todayDiet.intakeKcal > 0 ? "neutral" : "warn",
      reasonShort: todayDiet.intakeKcal > 0 ? "오늘 기록이 있어요" : "오늘 기록이 아직 없어요",
      value: `${formatNumber(todayDiet.intakeKcal)}/${formatNumber(todayDiet.targetKcal)}kcal`,
      ctaLabel: "식단 기록",
      onClick: () => setActiveTab("diet"),
    },
  ];

  // 날짜 클릭 핸들러 - 미래 날짜 검증
  const handleDateClick = async (dateObj: { dayLabel: string; date: number; isToday: boolean; fullDate: Date }) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const clickedDate = new Date(dateObj.fullDate);
    clickedDate.setHours(0, 0, 0, 0);

    // 미래 날짜 선택 시 경고
    if (clickedDate > today) {
      setShowFutureWarning(true);
      return;
    }

    // 로컬 날짜 문자열 (타임존 보정)
    const year = clickedDate.getFullYear();
    const month = String(clickedDate.getMonth() + 1).padStart(2, '0');
    const day = String(clickedDate.getDate()).padStart(2, '0');
    const dateStr = `${year}-${month}-${day}`;
    
    setSelectedDate(clickedDate);
    
    // 건강 데이터 로드
    const healthData = await loadHealthByDate(dateStr);
    setSelectedDateData(healthData);
    
    // 복약 데이터 로드
    const medsData = await loadMedsByDate(dateStr);
    setSelectedDateMeds(medsData);
    
    console.log('[handleDateClick] Selected date:', dateStr, 'health:', healthData, 'meds:', medsData);
  };

  // 주간 이동 핸들러
  const handleWeekNavigation = (direction: 'prev' | 'next') => {
    const newDate = new Date(weekStartDate);
    const offset = direction === 'next' ? 7 : -7;
    newDate.setDate(newDate.getDate() + offset);
    setWeekStartDate(newDate);
  };

  // 주간 달력 - weekStartDate 기준
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  const week = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(weekStartDate);
    d.setDate(weekStartDate.getDate() + i);
    const isToday = d.toDateString() === today.toDateString();
    const dayLabel = ["일", "월", "화", "수", "목", "금", "토"][d.getDay()];
    const isSelected = selectedDate && d.toDateString() === selectedDate.toDateString();
    return { dayLabel, date: d.getDate(), isToday, fullDate: d, isSelected };
  });

  // 선택된 날짜 포맷팅 (2026년 01월 28일 (수요일))
  const getFormattedSelectedDate = () => {
    const displayDate = selectedDate || today;
    const year = displayDate.getFullYear();
    const month = String(displayDate.getMonth() + 1).padStart(2, '0');
    const date = String(displayDate.getDate()).padStart(2, '0');
    const dayLabels = ["일요일", "월요일", "화요일", "수요일", "목요일", "금요일", "토요일"];
    const dayLabel = dayLabels[displayDate.getDay()];
    return `${year}년 ${month}월 ${date}일 (${dayLabel})`;
  };

  // ===== 탭별 화면 전환 (풀기능 흐름: 탭 클릭하면 실제로 바뀜) =====
  if (activeTab === "meds") {
    return (
      <>
        <CommonHeader
          onLogoClick={() => setActiveTab("home")}
          onDiary={() => {
            if (!isLoggedIn) {
              setShowLoginModal(true);
            } else {
              setShowDiary(true);
            }
          }}
          onFamily={() => {
            if (!isLoggedIn) {
              setShowLoginModal(true);
            }
          }}
          onShare={() => setShowShare(true)}
          onSettings={() => {
            if (!isLoggedIn) {
              setShowLoginModal(true);
            } else {
              setActiveTab('settings');
            }
          }}
          isLoggedIn={isLoggedIn}
          user={user}
          onLogout={() => logout()}
        />
        <div className="pb-28">
          <MedsPage onBack={() => setActiveTab("home")} />
        </div>
        <BottomTabNavigation activeTab={activeTab} onTabChange={setActiveTab} isLoggedIn={isLoggedIn} onLoginRequired={() => setShowLoginModal(true)} />
      </>
    );
  }

  if (activeTab === "diet") {
    return (
      <>
        <CommonHeader
          onLogoClick={() => setActiveTab("home")}
          onDiary={() => {
            if (!isLoggedIn) {
              setShowLoginModal(true);
            } else {
              setShowDiary(true);
            }
          }}
          onFamily={() => {
            if (!isLoggedIn) {
              setShowLoginModal(true);
            }
          }}
          onShare={() => setShowShare(true)}
          onSettings={() => {
            if (!isLoggedIn) {
              setShowLoginModal(true);
            } else {
              setActiveTab('settings');
            }
          }}
          isLoggedIn={isLoggedIn}
          user={user}
          onLogout={() => logout()}
        />
        <div className="pb-28">
          <DietPage onBack={() => setActiveTab("home")} />
        </div>
        <BottomTabNavigation activeTab={activeTab} onTabChange={setActiveTab} isLoggedIn={isLoggedIn} onLoginRequired={() => setShowLoginModal(true)} />
      </>
    );
  }

  if (activeTab === "measure") {
    return (
      <>
        <CommonHeader
          onLogoClick={() => setActiveTab("home")}
          onDiary={() => {
            if (!isLoggedIn) {
              setShowLoginModal(true);
            } else {
              setShowDiary(true);
            }
          }}
          onFamily={() => {
            if (!isLoggedIn) {
              setShowLoginModal(true);
            }
          }}
          onShare={() => setShowShare(true)}
          onSettings={() => {
            if (!isLoggedIn) {
              setShowLoginModal(true);
            } else {
              setActiveTab('settings');
            }
          }}
          isLoggedIn={isLoggedIn}
          user={user}
          onLogout={() => logout()}
        />
        <div className="pb-28">
          <MeasurePage onBack={() => setActiveTab("home")} />
        </div>
        <BottomTabNavigation activeTab={activeTab} onTabChange={setActiveTab} isLoggedIn={isLoggedIn} onLoginRequired={() => setShowLoginModal(true)} />
      </>
    );
  }
  
  if (activeTab === "walk") {
    return (
      <>
        <CommonHeader
          onLogoClick={() => setActiveTab("home")}
          onDiary={() => {
            if (!isLoggedIn) {
              setShowLoginModal(true);
            } else {
              setShowDiary(true);
            }
          }}
          onFamily={() => {
            if (!isLoggedIn) {
              setShowLoginModal(true);
            }
          }}
          onShare={() => setShowShare(true)}
          onSettings={() => {
            if (!isLoggedIn) {
              setShowLoginModal(true);
            } else {
              setActiveTab('settings');
            }
          }}
          isLoggedIn={isLoggedIn}
          user={user}
          onLogout={() => logout()}
        />
        <div className="pb-28">
          <WalkPage 
            onBack={() => setActiveTab("home")} 
            stepsCount={todaySteps.current}
            stepsGoal={todaySteps.goal}
          />
        </div>
        <BottomTabNavigation activeTab={activeTab} onTabChange={setActiveTab} isLoggedIn={isLoggedIn} onLoginRequired={() => setShowLoginModal(true)} />
      </>
    );
  }

  if (activeTab === "settings") {
    return (
      <>
        <CommonHeader
          onLogoClick={() => setActiveTab("home")}
          onDiary={() => {
            if (!isLoggedIn) {
              setShowLoginModal(true);
            } else {
              setShowDiary(true);
            }
          }}
          onFamily={() => {
            if (!isLoggedIn) {
              setShowLoginModal(true);
            }
          }}
          onShare={() => setShowShare(true)}
          onSettings={() => {
            if (!isLoggedIn) {
              setShowLoginModal(true);
            } else {
              setActiveTab('settings');
            }
          }}
          isLoggedIn={isLoggedIn}
          user={user}
          onLogout={() => logout()}
        />
        <div className="pb-28">
          <SettingsPage 
            onBack={() => setActiveTab("home")}
            onMenuClick={(menu: string) => {
              if (menu === "my-info") {
                setActiveTab("my-info");
              } else if (menu === "personal-settings") {
                setActiveTab("personal-settings");
              } else if (menu === "health-diary") {
                setActiveTab("health-diary");
              } else if (menu === "body-measurements") {
                setActiveTab("body-measurements");
              } else if (menu === "customer-center") {
                setActiveTab("customer-center");
              } else if (menu === "environment-settings") {
                setActiveTab("environment-settings");
              } else if (menu === "event-notice") {
                setActiveTab("event-notice-list");
              } else if (menu === "caddy-settlement") {
                setActiveTab("medication-history");
              } else if (menu === "caddy-coupon") {
                setActiveTab("diet-history");
              } else if (menu === "caddy-payment") {
                setActiveTab("blood-records");
              } else {
                console.log('[Herbotai] Menu clicked:', menu);
              }
            }}
          />
        </div>
        <BottomTabNavigation activeTab={activeTab} onTabChange={setActiveTab} isLoggedIn={isLoggedIn} onLoginRequired={() => setShowLoginModal(true)} />
      </>
    );
  }

  if (activeTab === "customer-center") {
    return (
      <>
        <CommonHeader
          onLogoClick={() => setActiveTab("home")}
          onDiary={() => {
            if (!isLoggedIn) {
              setShowLoginModal(true);
            } else {
              setShowDiary(true);
            }
          }}
          onFamily={() => {
            if (!isLoggedIn) {
              setShowLoginModal(true);
            }
          }}
          onShare={() => setShowShare(true)}
          onSettings={() => {
            if (!isLoggedIn) {
              setShowLoginModal(true);
            } else {
              setActiveTab('settings');
            }
          }}
          isLoggedIn={isLoggedIn}
          user={user}
          onLogout={() => logout()}
        />
        <div className="pb-28">
          <CustomerCenterPage 
            onBack={() => setActiveTab("settings")}
            onMenuClick={(menu: string) => {
              console.log('[Herbotai] Customer center menu clicked:', menu);
            }}
          />
        </div>
        <BottomTabNavigation activeTab={activeTab} onTabChange={setActiveTab} isLoggedIn={isLoggedIn} onLoginRequired={() => setShowLoginModal(true)} />
      </>
    );
  }

  if (activeTab === "personal-settings") {
    return (
      <>
        <CommonHeader
          onLogoClick={() => setActiveTab("home")}
          onDiary={() => {
            if (!isLoggedIn) {
              setShowLoginModal(true);
            } else {
              setShowDiary(true);
            }
          }}
          onFamily={() => {
            if (!isLoggedIn) {
              setShowLoginModal(true);
            }
          }}
          onShare={() => setShowShare(true)}
          onSettings={() => {
            if (!isLoggedIn) {
              setShowLoginModal(true);
            } else {
              setActiveTab('settings');
            }
          }}
          isLoggedIn={isLoggedIn}
          user={user}
          onLogout={() => logout()}
        />
        <div className="pb-28">
          <PersonalSettingsPage 
            onBack={() => setActiveTab("settings")}
          />
        </div>
        <BottomTabNavigation activeTab={activeTab} onTabChange={setActiveTab} isLoggedIn={isLoggedIn} onLoginRequired={() => setShowLoginModal(true)} />
      </>
    );
  }

  if (activeTab === "environment-settings") {
    return (
      <>
        <CommonHeader
          onLogoClick={() => setActiveTab("home")}
          onDiary={() => {
            if (!isLoggedIn) {
              setShowLoginModal(true);
            } else {
              setShowDiary(true);
            }
          }}
          onFamily={() => {
            if (!isLoggedIn) {
              setShowLoginModal(true);
            }
          }}
          onShare={() => setShowShare(true)}
          onSettings={() => {
            if (!isLoggedIn) {
              setShowLoginModal(true);
            } else {
              setActiveTab('settings');
            }
          }}
          isLoggedIn={isLoggedIn}
          user={user}
          onLogout={() => logout()}
        />
        <div className="pb-28">
          <EnvironmentSettingsPage 
            onBack={() => setActiveTab("settings")}
            onMenuClick={(menu: string) => {
              console.log('[Herbotai] Environment settings menu clicked:', menu);
            }}
          />
        </div>
        <BottomTabNavigation activeTab={activeTab} onTabChange={setActiveTab} isLoggedIn={isLoggedIn} onLoginRequired={() => setShowLoginModal(true)} />
      </>
    );
  }

  if (activeTab === "event-notice-list") {
    return (
      <>
        <CommonHeader
          onLogoClick={() => setActiveTab("home")}
          onDiary={() => {
            if (!isLoggedIn) {
              setShowLoginModal(true);
            } else {
              setShowDiary(true);
            }
          }}
          onFamily={() => {
            if (!isLoggedIn) {
              setShowLoginModal(true);
            }
          }}
          onShare={() => setShowShare(true)}
          onSettings={() => {
            if (!isLoggedIn) {
              setShowLoginModal(true);
            } else {
              setActiveTab('settings');
            }
          }}
          isLoggedIn={isLoggedIn}
          user={user}
          onLogout={() => logout()}
        />
        <div className="pb-28">
          <EventNoticeListPage 
            onBack={() => setActiveTab("settings")}
            onItemClick={(id: string) => {
              console.log('[Herbotai] Event notice item clicked:', id);
            }}
          />
        </div>
        <BottomTabNavigation activeTab={activeTab} onTabChange={setActiveTab} isLoggedIn={isLoggedIn} onLoginRequired={() => setShowLoginModal(true)} />
      </>
    );
  }

  if (activeTab === "medication-history") {
    return (
      <>
        <CommonHeader
          onLogoClick={() => setActiveTab("home")}
          onDiary={() => {
            if (!isLoggedIn) {
              setShowLoginModal(true);
            } else {
              setShowDiary(true);
            }
          }}
          onFamily={() => {
            if (!isLoggedIn) {
              setShowLoginModal(true);
            }
          }}
          onShare={() => setShowShare(true)}
          onSettings={() => {
            if (!isLoggedIn) {
              setShowLoginModal(true);
            } else {
              setActiveTab('settings');
            }
          }}
          isLoggedIn={isLoggedIn}
          user={user}
          onLogout={() => logout()}
        />
        <div className="pb-28">
          <MedicationHistoryPage 
            onBack={() => setActiveTab("settings")}
          />
        </div>
        <BottomTabNavigation activeTab={activeTab} onTabChange={setActiveTab} isLoggedIn={isLoggedIn} onLoginRequired={() => setShowLoginModal(true)} />
      </>
    );
  }

  if (activeTab === "diet-history") {
    return (
      <>
        <CommonHeader
          onLogoClick={() => setActiveTab("home")}
          onDiary={() => {
            if (!isLoggedIn) {
              setShowLoginModal(true);
            } else {
              setShowDiary(true);
            }
          }}
          onFamily={() => {
            if (!isLoggedIn) {
              setShowLoginModal(true);
            }
          }}
          onShare={() => setShowShare(true)}
          onSettings={() => {
            if (!isLoggedIn) {
              setShowLoginModal(true);
            } else {
              setActiveTab('settings');
            }
          }}
          isLoggedIn={isLoggedIn}
          user={user}
          onLogout={() => logout()}
        />
        <div className="pb-28">
          <DietHistoryPage 
            onBack={() => setActiveTab("settings")}
          />
        </div>
        <BottomTabNavigation activeTab={activeTab} onTabChange={setActiveTab} isLoggedIn={isLoggedIn} onLoginRequired={() => setShowLoginModal(true)} />
      </>
    );
  }

  if (activeTab === "health-diary") {
    return (
      <>
        <CommonHeader
          onLogoClick={() => setActiveTab("home")}
          onDiary={() => {
            if (!isLoggedIn) {
              setShowLoginModal(true);
            } else {
              setShowDiary(true);
            }
          }}
          onFamily={() => {
            if (!isLoggedIn) {
              setShowLoginModal(true);
            }
          }}
          onShare={() => setShowShare(true)}
          onSettings={() => {
            if (!isLoggedIn) {
              setShowLoginModal(true);
            } else {
              setActiveTab('settings');
            }
          }}
          isLoggedIn={isLoggedIn}
          user={user}
          onLogout={() => logout()}
        />
        <div className="pb-28">
          <HealthDiaryPage onBack={() => setActiveTab('settings')} />
        </div>
        <BottomTabNavigation activeTab={activeTab} onTabChange={setActiveTab} isLoggedIn={isLoggedIn} onLoginRequired={() => setShowLoginModal(true)} />
      </>
    );
  }

  if (activeTab === "blood-records") {
    return (
      <>
        <CommonHeader
          onLogoClick={() => setActiveTab("home")}
          onDiary={() => {
            if (!isLoggedIn) {
              setShowLoginModal(true);
            } else {
              setShowDiary(true);
            }
          }}
          onFamily={() => {
            if (!isLoggedIn) {
              setShowLoginModal(true);
            }
          }}
          onShare={() => setShowShare(true)}
          onSettings={() => {
            if (!isLoggedIn) {
              setShowLoginModal(true);
            } else {
              setActiveTab('settings');
            }
          }}
          isLoggedIn={isLoggedIn}
          user={user}
          onLogout={() => logout()}
        />
        <div className="pb-28">
          <BloodRecordsPage 
            onBack={() => setActiveTab("settings")}
          />
        </div>
        <BottomTabNavigation activeTab={activeTab} onTabChange={setActiveTab} isLoggedIn={isLoggedIn} onLoginRequired={() => setShowLoginModal(true)} />
      </>
    );
  }

  if (activeTab === "my-info") {
    return (
      <>
        <CommonHeader
          onLogoClick={() => setActiveTab("home")}
          onDiary={() => {
            if (!isLoggedIn) {
              setShowLoginModal(true);
            } else {
              setShowDiary(true);
            }
          }}
          onFamily={() => {
            if (!isLoggedIn) {
              setShowLoginModal(true);
            }
          }}
          onShare={() => setShowShare(true)}
          onSettings={() => {
            if (!isLoggedIn) {
              setShowLoginModal(true);
            } else {
              setActiveTab('settings');
            }
          }}
          isLoggedIn={isLoggedIn}
          user={user}
          onLogout={() => logout()}
        />
        <div className="pb-28">
          <MyInfoPage 
            onBack={() => setActiveTab("settings")}
            onLogout={() => setActiveTab("home")}
          />
        </div>
        <BottomTabNavigation activeTab={activeTab} onTabChange={setActiveTab} isLoggedIn={isLoggedIn} onLoginRequired={() => setShowLoginModal(true)} />
      </>
    );
  }

  if (activeTab === "body-measurements") {
    return (
      <>
        <CommonHeader
          onLogoClick={() => setActiveTab("home")}
          onDiary={() => {
            if (!isLoggedIn) {
              setShowLoginModal(true);
            } else {
              setShowDiary(true);
            }
          }}
          onFamily={() => {
            if (!isLoggedIn) {
              setShowLoginModal(true);
            }
          }}
          onShare={() => setShowShare(true)}
          onSettings={() => {
            if (!isLoggedIn) {
              setShowLoginModal(true);
            } else {
              setActiveTab('settings');
            }
          }}
          isLoggedIn={isLoggedIn}
          user={user}
          onLogout={() => logout()}
        />
        <div className="pb-28">
          <BodyMeasurementsPage 
            onBack={() => setActiveTab("settings")}
          />
        </div>
        <BottomTabNavigation activeTab={activeTab} onTabChange={setActiveTab} isLoggedIn={isLoggedIn} onLoginRequired={() => setShowLoginModal(true)} />
      </>
    );
  }

  // ===== 홈 화면 =====
  return (
    <>
      <CommonHeader
        onLogoClick={() => setActiveTab("home")}
        onDiary={() => {
          if (!isLoggedIn) {
            setShowLoginModal(true);
          } else {
            setShowDiary(true);
          }
        }}
        onFamily={() => {
          if (!isLoggedIn) {
            setShowLoginModal(true);
          }
        }}
        onShare={() => setShowShare(true)}
        onSettings={() => {
          if (!isLoggedIn) {
            setShowLoginModal(true);
          } else {
            setActiveTab('settings');
          }
        }}
        isLoggedIn={isLoggedIn}
        user={user}
        onLogout={() => logout()}
      />

      <ProtectedContent>
        <div className="w-full max-w-md mx-auto min-h-screen bg-slate-100 pb-28 relative font-[system-ui] pt-3 -mt-px">
          {/* 🆕 Health Advice Card - AI 건강 조언 또는 프로필 기반 조언 */}
          <HealthAdviceCard />

      {/* 🆕 AI 챗봇 컴포넌트 */}
      <div className="mx-4 mt-3" style={{ height: "450px", display: "flex" }}>
        <AIChatbotComponent isLoggedIn={isLoggedIn} />
      </div>

      {/* 주간 달력 — 날짜 감각 보조 */}
      <section className="mx-4 mt-3 rounded-2xl bg-white border border-slate-200 px-3 py-2 shadow-sm">
        <div className="flex items-center justify-between px-1 pb-2">
          <p className="text-[12px] font-semibold text-slate-900">날짜별 기록 보기</p>
          <span className="text-[10px] text-slate-400">{getFormattedSelectedDate()}</span>
        </div>
        
        {/* 주간 네비게이션 */}
        <div className="flex items-center justify-between gap-2 mb-2">
          <button
            type="button"
            onClick={() => handleWeekNavigation('prev')}
            className="px-2 py-1 rounded-lg hover:bg-slate-100 transition-colors"
            title="이전 주"
          >
            <span className="text-lg">←</span>
          </button>
          
          <div className="flex justify-between gap-1 flex-1">
            {week.map((d) => (
              <button
                key={`${d.dayLabel}-${d.date}`}
                type="button"
                onClick={() => handleDateClick(d)}
                className={`flex flex-col items-center justify-center flex-1 rounded-xl py-1.5 transition-colors ${
                  d.isSelected ? "bg-orange-100 text-orange-700 font-semibold" : d.isToday ? "bg-violet-50 text-violet-700 font-semibold" : "text-slate-700 hover:bg-slate-50"
                }`}
              >
                <span className="text-[11px]">{d.dayLabel}</span>
                <span className="text-[14px] mt-0.5">{d.date}</span>
              </button>
            ))}
          </div>

          <button
            type="button"
            onClick={() => handleWeekNavigation('next')}
            className="px-2 py-1 rounded-lg hover:bg-slate-100 transition-colors"
            title="다음 주"
          >
            <span className="text-lg">→</span>
          </button>
        </div>
      </section>

      {/* 미래 날짜 경고 모달 */}
      {showFutureWarning && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 px-4">
          <div className="bg-white rounded-2xl px-6 py-5 shadow-lg max-w-sm w-full">
            <p className="text-[14px] font-semibold text-slate-900 mb-2">⚠️ 미래 날짜 선택</p>
            <p className="text-[12px] text-slate-600 mb-4">아직 기록되지 않은 날짜입니다.</p>
            <p className="text-[12px] text-slate-600 mb-5">오늘 이전의 날짜만 선택할 수 있어요.</p>
            <button
              type="button"
              onClick={() => setShowFutureWarning(false)}
              className="w-full px-4 py-2 bg-violet-600 text-white rounded-lg font-semibold text-[12px] hover:bg-violet-700 transition-colors"
            >
              확인
            </button>
          </div>
        </div>
      )}

      {/* 중복 제거 핵심: '지금 먼저 확인할 것(오늘의 건강미션)' 단일 카드 */}
      <NextActionsCard title="지금 먼저 확인할 것(오늘의 건강미션)" subtitle={`오늘 한 번만 확인하면 되는 항목입니다. ${todayLabel}`} items={nextActions} />

      {/* 가족/안전 신호 */}
      {/* <FamilyAlertsCard alerts={familyAlerts} onMarkRead={handleMarkAlertRead} /> */}

      {/* 오늘의 건강요약 — 결론 */}
      {/* <TodayHealthSummaryCard summary={todaySummary} showWhy={showWhy} onToggleWhy={() => setShowWhy((v) => !v)} /> */}

      {/* 꾸준함 점수보기 — 후면(접힘) */}
      {/* <ConsistencyPointsCard
        points={points}
        collapsed={pointsCollapsed}
        onToggleCollapse={() => setPointsCollapsed((v) => !v)}
        onToggleMission={handleToggleMission}
        onAddPoints={handleAddPoints}
      /> */}

      

      {/* 건강 일기 모달 */}
      <HealthDiaryModal isOpen={showDiary} onClose={() => setShowDiary(false)} />

      {/* 공유 바텀시트 */}
      {showShare && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-end justify-center" onClick={() => setShowShare(false)}>
          <div className="w-full max-w-md rounded-t-2xl bg-white px-5 pt-2 pb-5 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="w-10 h-1 rounded-full bg-slate-300 mx-auto mb-3" />
            <div className="flex items-center justify-between mb-1">
              <span className="text-[14px] font-semibold text-slate-900">공유</span>
              <button className="text-[12px] text-slate-500" type="button" onClick={() => setShowShare(false)}>
                닫기
              </button>
            </div>
            <p className="text-[11px] text-slate-500 mb-3">오늘 기록을 가족/보호자에게 공유할 수 있어요.</p>
            <div className="grid grid-cols-4 gap-3">
              {[
                { label: "카카오", icon: "💬" },
                { label: "문자", icon: "📱" },
                { label: "이메일", icon: "✉️" },
                { label: "기타 앱", icon: "📤" },
              ].map((x) => (
                <button key={x.label} className="flex flex-col items-center gap-1 text-[11px] text-slate-600" type="button">
                  <span className="w-11 h-11 rounded-full bg-violet-50 flex items-center justify-center text-[22px]">{x.icon}</span>
                  <span>{x.label}</span>
                </button>
              ))}
            </div>
            {/* <p className="mt-3 text-[10px] text-slate-400">실제 서비스에서는 Web Share API/카카오 SDK 연동으로 바로 전송되도록 구현할 수 있습니다.</p> */}
          </div>
        </div>
      )}

      {/* Footer 활성 탭 결정 함수 */}
      <BottomTabNavigation activeTab={activeTab} onTabChange={setActiveTab} isLoggedIn={isLoggedIn} onLoginRequired={() => setShowLoginModal(true)} />

      {/* 로그인 모달 */}
      <LoginModal />
        </div>
      </ProtectedContent>
    </>
  );
}

export default function HerbyMainHomeV1Merged() {
  return (
    <AuthProvider>
      <HerbyMainHomeContent />
    </AuthProvider>
  );
}
